import { type QuestionnaireQuestionType } from "api-client/legacy-models";

import { BaseQuestion } from "src/questionnaire/BaseQuestion";
import { Confirm } from "src/questionnaire/Confirm";
import { LinearScaleQuestion } from "src/questionnaire/LinearScaleQuestion";
import { LongText } from "src/questionnaire/LongText";
import { MultipleChoice } from "src/questionnaire/MultipleChoice";
import { getQuestionnaireDisplayRange } from "src/questionnaire/questionnaireDisplayRange";
import { SingleChoice } from "src/questionnaire/SingleChoice";
import { Text } from "src/questionnaire/Text";

interface QuestionnaireItemRendererProps {
  item: {
    question?: {
      id: string;
      title: string;
      required: boolean;
      description: string;
      options: {
        label: string;
        id: string;
      }[];
      hasOtherOption: boolean;
      type: QuestionnaireQuestionType;
      metric?: {
        questionnaireDisplayConfig?: unknown | null;
      } | null;
    } | null;
    section?: {
      title: string;
      description: string;
    } | null;
  };
  value?: {
    value?: string;
    optionIds?: string[];
    confirmed?: boolean;
    otherOptionValue?: string;
  };
  onChange?: (value: {
    value?: string;
    optionIds?: string[];
    confirmed?: boolean;
    otherOptionValue?: string;
  }) => void;
  disabled?: boolean;
  error?: string;
}

export function QuestionnaireItemRenderer(
  props: QuestionnaireItemRendererProps,
) {
  const { item, value, onChange } = props;

  const handleChange = (update: {
    value?: string;
    optionIds?: string[];
    confirmed?: boolean;
    otherOptionValue?: string;
  }) => {
    onChange?.(update);
  };

  // Render section
  if (item.section) {
    return (
      <div className="bg-background">
        <p className="">{item.section.title}</p>
        {item.section.description && (
          <p className="text-sm text-muted-foreground">
            {item.section.description}
          </p>
        )}
      </div>
    );
  }

  // Skip if no question
  if (!item.question) {
    return null;
  }

  const question = item.question;
  const linearScaleRange = getQuestionnaireDisplayRange(
    question.metric?.questionnaireDisplayConfig,
  );

  return (
    <div className="bg-background">
      <BaseQuestion
        id={question.id}
        prompt={question.title}
        required={question.required}
        error={props.error}
      >
        {question.type === "SHORT_ANSWER" && (
          <Text
            id={question.id}
            value={value?.value || ""}
            onChange={(newValue) => {
              handleChange({
                value: newValue,
              });
            }}
            disabled={props.disabled}
          />
        )}

        {question.type === "LONG_ANSWER" && (
          <LongText
            id={question.id}
            value={value?.value || ""}
            onChange={(newValue) => {
              handleChange({
                value: newValue,
              });
            }}
            disabled={props.disabled}
          />
        )}

        {question.type === "SINGLE_CHOICE" && (
          <SingleChoice
            value={value?.optionIds?.[0] || ""}
            onChange={(newValue) => {
              handleChange({
                optionIds: newValue ? [newValue] : [],
              });
            }}
            options={question.options.map((option) => ({
              label: option.label,
              value: option.id,
            }))}
            hasOtherOption={question.hasOtherOption}
            otherOptionValue={value?.otherOptionValue || ""}
            onOtherOptionValueChange={(newValue) => {
              handleChange({
                optionIds: value?.optionIds || [],
                otherOptionValue: newValue,
              });
            }}
            disabled={props.disabled}
          />
        )}

        {question.type === "MULTIPLE_CHOICE" && (
          <MultipleChoice
            value={value?.optionIds || []}
            onChange={(newValues) => {
              handleChange({
                optionIds: newValues,
              });
            }}
            options={question.options.map((option) => ({
              label: option.label,
              value: option.id,
            }))}
            hasOtherOption={question.hasOtherOption}
            otherOptionValue={value?.otherOptionValue || ""}
            onOtherOptionValueChange={(newValue) => {
              handleChange({
                optionIds: value?.optionIds || [],
                otherOptionValue: newValue,
              });
            }}
            disabled={props.disabled}
          />
        )}

        {question.type === "LINEAR_SCALE" && (
          <LinearScaleQuestion
            id={question.id}
            description={question.description}
            value={getLinearScaleValue(value?.value)}
            min={linearScaleRange.min}
            max={linearScaleRange.max}
            step={linearScaleRange.step}
            onChange={(newValue) => {
              handleChange({
                value: String(newValue),
              });
            }}
            disabled={props.disabled}
          />
        )}

        {question.type === "CONFIRM" && (
          <Confirm
            id={question.id}
            description={question.description}
            checked={value?.confirmed || false}
            onChange={(newValue) => {
              handleChange({
                confirmed: newValue,
              });
            }}
            disabled={props.disabled}
          />
        )}
      </BaseQuestion>
    </div>
  );
}

function getLinearScaleValue(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return undefined;
  }

  return numericValue;
}
